import { createSelector } from '@ngrx/store';
import { ChartPeriodDates, DepotChartSerie } from '../entities/chart-and-stats.entity';
import { Filter, Trade } from '../entities/trade.entity';
import { WeightingType } from '../entities/weighting.entity';
import { statisticsFeature } from './statistics.reducer';

const top10 = (trades: Trade[]) => (Array.isArray(trades) ? trades.slice(0, 10) : []);

const tradeTicker = (trades: Trade[], filter: Filter) =>
  top10(filter && Array.isArray(trades) ? trades.filter(({ instrumentType }) => instrumentType === filter) : trades);

const getYear = (date: Date) => date.getFullYear();

const filterChart = (chart: DepotChartSerie[], chartPeriodDates: ChartPeriodDates, accountTotal: number, cashTotal?: number) => {
  if (!chart) {
    return null;
  }

  if (!chartPeriodDates?.to || getYear(chartPeriodDates.to) === getYear(new Date())) {
    chart = [
      ...chart,
      {
        total: accountTotal + (cashTotal ?? 0),
        date: new Date().toJSON(),
      },
    ];
  }

  return chartPeriodDates?.from && chartPeriodDates?.to
    ? chart.filter((item) => new Date(item.date) >= chartPeriodDates.from && new Date(item.date) <= chartPeriodDates.to)
    : chart;
};

export const selectTradeTicker = createSelector(statisticsFeature.selectTrades, statisticsFeature.selectTradeTickerFilter, tradeTicker);

export const selectWeightingLoading = createSelector(
  statisticsFeature.selectWeightingByAssetTypeLoading,
  statisticsFeature.selectWeightingByTopPositionsLoading,
  (weightingByAssetTypeLoading, weightingByTopPositionsLoading) => weightingByAssetTypeLoading || weightingByTopPositionsLoading,
);

export const selectWeighting = createSelector(
  statisticsFeature.selectWeightingByAssetType,
  statisticsFeature.selectWeightingByTopPositions,
  statisticsFeature.selectWeightingByPositions,
  statisticsFeature.selectWeightingType,
  (weightingByAssetType, weightingTopPositions, weightingByPositions, filter) => {
    switch (filter) {
      case WeightingType.ASSETS:
        return weightingByAssetType;
      case WeightingType.TOP_POSITIONS:
        return weightingTopPositions;
      case WeightingType.ALL_POSITIONS:
        return weightingByPositions;
      default:
        return null;
    }
  },
);

export const selectMyChartWithCash = createSelector(
  statisticsFeature.selectChartWithCash,
  statisticsFeature.selectChartPeriodDates,
  statisticsFeature.selectPerformance,
  statisticsFeature.selectStats,
  (chart, chartPeriodDates, performance, stats) => filterChart(chart, chartPeriodDates, performance?.currentValue, stats?.cashTotal),
);

export const selectMyChartDepotWithCash = createSelector(
  statisticsFeature.selectChartWithCash,
  statisticsFeature.selectPerformance,
  statisticsFeature.selectStats,
  (chart, performance, stats) =>
    filterChart(
      chart,
      { from: new Date(new Date().getFullYear() - 1, new Date().getMonth(), new Date().getDate()), to: null },
      performance?.currentValue,
      stats?.cashTotal,
    ),
);

export const selectMyChartWithoutCash = createSelector(
  statisticsFeature.selectChartWithoutCash,
  statisticsFeature.selectChartPeriodDates,
  statisticsFeature.selectPerformance,
  (chart, chartPeriodDates, performance) => filterChart(chart, chartPeriodDates, performance?.currentValue),
);

export const selectStats = createSelector(statisticsFeature.selectStats, statisticsFeature.selectPerformance, (stats, performance) => {
  if (!stats || !performance) {
    return null;
  }

  const { currentValue } = performance;
  const { cashTotal, cashPayInTotal, cashPayOutTotal, portfolioTransferTotal, performancePercentage } = stats;
  const ratio = cashPayInTotal - cashPayOutTotal + portfolioTransferTotal;
  const accountTotalWithCash = currentValue + cashTotal;

  return {
    ...stats,
    performancePercentage: ratio ? (accountTotalWithCash / ratio - 1) * 100 : performancePercentage,
  };
});

export const selectDividendYields = createSelector(statisticsFeature.selectDividendYields, (dividendYields) => {
  if (!dividendYields) {
    return null;
  }

  return dividendYields.map((dividendYield) => {
    if (!dividendYield) {
      return null;
    }
    const { receivedPeriods, forecastedPeriods, receivedDividendTotal } = dividendYield;
    const hasTotal = receivedDividendTotal?.dividendAbsolute !== null || receivedDividendTotal?.dividendYield !== null;

    if (hasTotal) {
      return dividendYield;
    }

    if (
      (receivedPeriods?.length && receivedPeriods.some((period) => period.dividendAbsolute !== null || period.dividendYield !== null)) ||
      (forecastedPeriods?.length && forecastedPeriods.some((period) => period.dividendAbsolute !== null))
    ) {
      return dividendYield;
    }

    return null;
  });
});
